import { Component, Input, ChangeDetectionStrategy, ViewEncapsulation } from '@angular/core';

/**
 * Badge color variants matching Figma design system:
 * - primary: Red (brand action color — note: Figma "primary" = red, not Bootstrap blue)
 * - secondary: Blue
 * - neutral: Gray
 * - success: Green
 * - warning: Amber/orange
 */
export type BadgeColor = 'primary' | 'secondary' | 'neutral' | 'success' | 'warning';

/** Badge size variants */
export type BadgeSize = 'large' | 'small';

/**
 * Badge variant (visual style):
 * - heavy: Solid background with white text
 * - light: Subtle background with dark text
 *
 * Figma property mapping: Figma "Style" → Angular "variant".
 * Convention: all design system components use "variant" as the input name
 * for their primary visual style property, regardless of Figma naming.
 */
export type BadgeVariant = 'heavy' | 'light';

/**
 * Badge Component
 *
 * A pill-shaped badge for counts, statuses, and labels.
 * Matches Figma node 5213-19715 (20 variants: 5 colors x 2 sizes x 2 styles).
 *
 * @example
 * ```html
 * <!-- Simple badge -->
 * <hc-badge color="primary" variant="heavy">3</hc-badge>
 *
 * <!-- Light style with icon -->
 * <hc-badge color="success" variant="light" [dispIcon]="true">
 *   <lucide-icon badgeIcon name="check" class="icon-sm"></lucide-icon>
 *   Active
 * </hc-badge>
 *
 * <!-- Small neutral -->
 * <hc-badge color="neutral" size="small" variant="light">Optional</hc-badge>
 * ```
 */
@Component({
  selector: 'hc-badge',
  standalone: false,
  templateUrl: './badge.component.html',
  styleUrl: './badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class BadgeComponent {
  /** Color variant — maps to Figma "Color" property */
  @Input() color: BadgeColor = 'neutral';

  /** Size variant — maps to Figma "Size" property */
  @Input() size: BadgeSize = 'small';

  /** Visual style — maps to Figma "Style" property */
  @Input() variant: BadgeVariant = 'light';

  /** Show icon slot — maps to Figma "dispIcon" property */
  @Input() dispIcon = false;

  /**
   * Show decorative checkbox — maps to Figma "dispCheckbox" property.
   * // TODO: dispCheckbox is currently decorative only. If interactive behavior is needed,
   * // add @Output() checkboxChange, role="checkbox", aria-checked, and click handler.
   */
  @Input() dispCheckbox = false;

  /** Combined CSS class string for the badge element */
  get badgeClass(): string {
    return [
      'hc-badge',
      `hc-badge--${this.color}`,
      `hc-badge--${this.size}`,
      `hc-badge--${this.variant}`
    ].join(' ');
  }
}
